// 볼륨 설정(마스터·BGM·효과음). localStorage 'dungeonhero.audio'에 저장한다.
// 설정 화면(src/ui/screens.ts)과 오디오 엔진이 이 모듈 하나를 함께 읽어 UI 계약을 늘리지 않는다.

export type VolumeKind = 'master' | 'bgm' | 'sfx';
export interface AudioVolumes {
  master: number;
  bgm: number;
  sfx: number;
}

export const AUDIO_KEY = 'dungeonhero.audio';
export const DEFAULT_VOLUMES: Readonly<AudioVolumes> = { master: 0.8, bgm: 0.6, sfx: 0.9 };

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** 저장된 문자열을 읽는다. 깨졌거나 범위를 벗어난 값은 기본값·0..1로 맞춘다. */
export function parseVolumes(raw: string | null): AudioVolumes {
  const v: AudioVolumes = { ...DEFAULT_VOLUMES };
  if (!raw) return v;
  try {
    const j = JSON.parse(raw) as Partial<Record<VolumeKind, unknown>>;
    for (const k of ['master', 'bgm', 'sfx'] as const) {
      const n = j[k];
      if (typeof n === 'number' && Number.isFinite(n)) v[k] = clamp01(n);
    }
  } catch {
    /* 깨진 값: 기본값 */
  }
  return v;
}

/** 슬라이더 값(0..1) → 음량 배율. 사람 귀에 고르게 들리도록 제곱 곡선을 쓴다. */
export function volumeToGain(v: number): number {
  const c = clamp01(v);
  return c * c;
}

function load(): AudioVolumes {
  try {
    return parseVolumes(typeof localStorage !== 'undefined' ? localStorage.getItem(AUDIO_KEY) : null);
  } catch {
    return { ...DEFAULT_VOLUMES };
  }
}

const current: AudioVolumes = load();
const listeners: ((kind: VolumeKind) => void)[] = [];

export function audioVolumes(): Readonly<AudioVolumes> {
  return current;
}

export function setAudioVolume(kind: VolumeKind, value: number): void {
  const v = clamp01(value);
  if (current[kind] === v) return;
  current[kind] = v;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(AUDIO_KEY, JSON.stringify(current));
  } catch {
    /* 저장 불가: 이번 실행에만 적용 */
  }
  for (const fn of listeners) fn(kind);
}

export function onVolumeChange(fn: (kind: VolumeKind) => void): void {
  listeners.push(fn);
}
